// Single source of truth for talking to the TALIT PMS backend.
//
// Historically the Express app served the React frontend AND the API from
// the same origin, so every `fetch('/api/...')` call in this codebase
// worked "by accident" via same-origin relative paths. The Cloudflare
// Workers + D1 backend and the (eventually Pages-hosted) frontend live on
// DIFFERENT origins, so every call must be resolved against the Worker's
// real base URL instead of a bare relative path.
//
// REACT_APP_API_URL is set per-environment in frontend/.env.development and
// frontend/.env.production. The empty-string fallback below preserves
// same-origin behavour ONLY for a local setup where the frontend happens to
// be proxied to the Worker on the same origin — the real intent is that the
// env files always set this explicitly.
export const API_BASE = process.env.REACT_APP_API_URL || '';

// Joins API_BASE with `path` regardless of whether `path` starts with a
// slash, and regardless of whether API_BASE has a trailing slash.
function buildUrl(path) {
  if (!API_BASE) return path; // same-origin fallback (dev-only edge case)
  const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

// Thin wrapper around fetch(): resolves `path` against API_BASE, attaches
// `Authorization: Bearer <token>` automatically when a token is present in
// localStorage, and merges any caller-supplied headers on top (so a caller
// can still add/override e.g. Content-Type, or omit auth by passing their
// own headers object without Authorization — note: to truly suppress auth,
// a caller would need to explicitly delete it after calling apiFetch, since
// this wrapper always attaches it when a token exists and no override is
// given).
//
// Returns the raw fetch Response — callers still do their own
// `.json()` / `.blob()` / status checks, matching how this codebase already
// calls fetch everywhere else. No magic auto-parsing.
export async function apiFetch(path, options = {}) {
  const url = buildUrl(path);
  const token = localStorage.getItem('token');

  const headers = { ...(options.headers || {}) };
  if (token && !('Authorization' in headers)) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return fetch(url, { ...options, headers });
}

// Resolves a Worker-relative asset URL (e.g. a tenant's logo_url or a
// guest's id_document_url) for direct use in an <img src> or similar —
// apiFetch itself returns a Response, not a usable asset URL, so callers
// that need the latter (not a fetch+parse) use this instead.
//
// NOTE: these URLs already start with /api/v1/... (they're built server-side
// in worker/lib/*.js as `/api/v1/tenants/logos/${key}` etc.), whereas
// apiFetch's `path` argument everywhere else in this codebase is a BARE path
// with no /api/v1 prefix (API_BASE already ends in /api/v1, so buildUrl()
// appends it there). Joining an already-prefixed asset URL onto API_BASE
// would duplicate the segment (.../api/v1/api/v1/...) and 404 — so this
// joins against the Worker's origin only, stripping API_BASE's own /api/v1
// suffix first.
export function resolveAssetUrl(relativeUrl) {
  if (!relativeUrl) return null;
  if (!API_BASE) return relativeUrl; // same-origin fallback (dev-only edge case)
  const origin = API_BASE.replace(/\/api\/v1\/?$/, '');
  const suffix = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;
  return `${origin}${suffix}`;
}
